import { useMemo, useState } from 'react';
import { ACTION_TYPE_OPTIONS } from '@/features/activity/activity.config';
import { ActivityFeedFilters, useActivityFeed } from '@/features/activity/activity.queries';
import { getActivityPath } from '@/features/activity/activity.utils';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { format } from 'date-fns';
import { Filter, X } from 'lucide-react';
import { DateRange } from 'react-day-picker';

import { ActivityActionType } from '@/types/activity.types';
import { Option } from '@/types/types';
import UserService from '@/services/user/user.service';
import { Button } from '@/components/ui/button';
import ActivityFeedList from '@/components/activity/activity-feed-list';
import { DateRangePresetInput } from '@/components/form-inputs/form-date-input';
import { SelectAsyncInput, SelectInput } from '@/components/select-input';

export const Route = createFileRoute('/_app/(activity)/activity/')({
  component: ActivityPage,
});

function ActivityPage() {
  const navigate = useNavigate();
  const [dateFilter, setDateFilter] = useState<{ from: string; to: string } | undefined>();
  const [actionTypes, setActionTypes] = useState<ActivityActionType[]>([]);
  const [actorIds, setActorIds] = useState<Option[]>([]);

  const filters = useMemo<ActivityFeedFilters>(
    () => ({
      fromDate: dateFilter?.from,
      toDate: dateFilter?.to,
      actionTypes: actionTypes.length ? actionTypes : undefined,
      actorIds: actorIds.length ? actorIds.map((o) => Number(o.value)) : undefined,
    }),
    [dateFilter, actionTypes, actorIds]
  );

  const hasFilters = Boolean(dateFilter || actionTypes.length || actorIds.length);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useActivityFeed({
    filters,
  });

  const items = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

  const fetchUserOptions = async () => {
    return UserService.dropdownOptions();
  };

  const actionTypeOptions = useMemo(
    () => ACTION_TYPE_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label })),
    []
  );

  const onDateRangeChange = (range: DateRange | undefined) => {
    if (!range?.from) {
      setDateFilter(undefined);
      return;
    }
    setDateFilter({
      from: format(range.from, 'yyyy-MM-dd'),
      to: range.to ? format(range.to, 'yyyy-MM-dd') : format(range.from, 'yyyy-MM-dd'),
    });
  };

  const onClearFilters = () => {
    setDateFilter(undefined);
    setActionTypes([]);
    setActorIds([]);
  };

  const onActivityItemClick = (item: Parameters<typeof getActivityPath>[0]) => {
    const path = getActivityPath(item);
    if (!path) return;
    navigate({ to: path });
  };

  return (
    <div className='flex h-full flex-col px-4 py-4'>
      <div className='mb-4 flex flex-col'>
        <span className='text-xl font-bold'>Δραστηριότητα</span>
        <span className='text-base font-light text-neutral-500'>
          Πρόσφατη δραστηριότητα διαχειριστή και συστήματος
        </span>
      </div>

      <div className='flex min-h-0 flex-1 flex-col overflow-hidden rounded-3xl border bg-white shadow-sm'>
        <div className='flex flex-wrap items-center gap-2 border-b px-4 py-3'>
          <Filter className='h-4 w-4 text-muted-foreground' />

          <DateRangePresetInput
            value={dateFilter}
            onClose={onDateRangeChange}
            className='h-9 w-[220px] text-xs'
            align='start'
          />

          <div className='w-[200px]'>
            <SelectInput
              isMulti
              binding='value'
              value={actionTypes}
              options={actionTypeOptions}
              onChange={(val) => setActionTypes((val as ActivityActionType[]) ?? [])}
              placeholder='Τύπος ενέργειας'
              isClearable
              inModal
            />
          </div>

          <div className='w-[200px]'>
            <SelectAsyncInput
              isMulti
              querykey='activity-users'
              value={actorIds}
              fetchOptions={fetchUserOptions}
              onChange={(val) => setActorIds((val as Option[]) ?? [])}
              placeholder='Χρήστης'
              isClearable
              inModal
            />
          </div>

          {hasFilters && (
            <Button
              variant='ghost'
              size='sm'
              onClick={onClearFilters}
              className='h-9 gap-1 text-xs text-muted-foreground'
            >
              <X className='h-3.5 w-3.5' />
              Καθαρισμός
            </Button>
          )}
        </div>

        <div className='min-h-0 flex-1 overflow-hidden'>
          <ActivityFeedList
            items={items}
            isLoading={isLoading}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={() => fetchNextPage()}
            onItemClick={onActivityItemClick}
            scrollClassName='h-full overflow-y-auto'
            groupByDay
          />
        </div>
      </div>
    </div>
  );
}
